import * as Sentry from '@sentry/angular';
import { CommonService, DialogService, LoggingService, LoginService } from '@pxl/models/contracts';
import { EnvironmentInjector, ErrorHandler, inject, Injectable, runInInjectionContext } from '@angular/core';
import {
    BAD_FEATURE_REQUEST,
    CONFIG_TOKEN,
    DialogableError,
    ERROR_DIALOG_TOKEN,
    PxlHttpResponseError,
    REQUEST_ABORTED_BY_CLIENT,
} from '@pxl/models/common';
import { HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { EMPTY, from } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { FORBIDDEN, INTERNAL_SERVER_ERROR, UNAUTHORIZED } from 'http-status-codes';
import { AppRoutingRouteNames } from '@pxl/route-names';
import { isChunkLoadError } from '@pxl/shared/util';
import { Router } from '@angular/router';

@Injectable()
export class CustomErrorHandler extends Sentry.SentryErrorHandler {
    private readonly loggingService: LoggingService = inject(LoggingService);
    private readonly dialogService = inject(DialogService);
    private readonly commonService = inject(CommonService);
    private readonly router = inject(Router);
    private readonly loginService = inject(LoginService);
    private readonly config = inject(CONFIG_TOKEN);
    private readonly errorDialogComponent = inject(ERROR_DIALOG_TOKEN);

    private readonly statusHandlers = new Map<number, (err: HttpErrorResponse) => void>([
        [FORBIDDEN, () => this.handleForbidden()],
        [UNAUTHORIZED, () => this.handleUnauthorized()],
        [BAD_FEATURE_REQUEST, (err) => this.handleBadFeatureRequest(err)],
        [REQUEST_ABORTED_BY_CLIENT, () => {}], // Aborted by client - most probably no error, do not log nor bother the user
        [INTERNAL_SERVER_ERROR, (err) => this.handleInternalServerError(err)],
    ]);

    public constructor() {
        super({ showDialog: false });
    }

    public override handleError(err: any): void {
        // HTTP errors
        if (err instanceof HttpErrorResponse) {
            const handled = this.handleExpectedHttpStatus(err);
            if (handled) return;

            this.logHttpError(err);
            return;
        }

        // Dialog-friendly errors
        if (err instanceof DialogableError) {
            this.dialogService.open(this.errorDialogComponent, {
                dialogTitle: err.dialogTitle,
                title: err.title,
                message: err.message,
            });
            return;
        }

        // Chunk load errors (PETSXL_FRONTEND-WX): lazy chunk failed to load (e.g. after a deployment or on a flaky
        // connection). Not reported to Sentry — recover via the error dialog; the CustomErrorHandler is the only
        // place that opens it.
        if (isChunkLoadError(err)) {
            this.loggingService.warn('Chunk load error detected, showing the error dialog:', err?.message || '');
            this.dialogService.open(this.errorDialogComponent);
        } else {
            if (this.config.sentryDsn) {
                super.handleError(err);
            } else {
                this.loggingService.error('Error caught by custom error handler:', err);
            }
        }
    }

    private handleExpectedHttpStatus(err: HttpErrorResponse): boolean {
        const handler = this.statusHandlers.get(err.status);
        if (handler) {
            handler(err);
            return true;
        }
        return false;
    }

    private handleInternalServerError(err: HttpErrorResponse): void {
        // Log and redirect to a dedicated error page to avoid broken UI state behind a dialog
        this.logHttpError(err);
        this.router.navigate(['/', AppRoutingRouteNames.Error], {
            queryParams: { code: err.status || 500 },
        });
    }

    private handleForbidden(): void {
        this.router.navigate(['/', AppRoutingRouteNames.NotFound]);
    }

    private handleUnauthorized(): void {
        this.loginService.logoutUser(false, true);
    }

    private handleBadFeatureRequest(err: HttpErrorResponse): void {
        this.logHttpError(err);
        this.performAppUpdateCheck();
    }

    private performAppUpdateCheck(): void {
        from(this.commonService.appUpdateCheck())
            .pipe(
                take(1),
                catchError((e) => {
                    this.loggingService.message('Error while checking app update', 'error', { error: e });
                    this.dialogService.open(this.errorDialogComponent);
                    return EMPTY;
                }),
            )
            .subscribe();
    }

    private logHttpError(err: HttpErrorResponse | PxlHttpResponseError) {
        const httpErr = err as HttpErrorResponse;
        let req: undefined | HttpRequest<unknown> = undefined;
        if (err instanceof PxlHttpResponseError) {
            req = err.request;
        }

        const method = req?.method ?? 'unknown-method';
        const requestUrl = req?.url ?? httpErr.url ?? '';
        const displayUrl = requestUrl.replace(this.config.API_BASE_PATH, '');

        this.loggingService.message(`Error ${err.status} while ${method} ${displayUrl}`, 'error', {
            request: {
                url: requestUrl,
                method: method,
                body: JSON.stringify(req?.body),
                params: req?.params?.toString?.() ?? '',
            },
            response: {
                status: httpErr.status,
                statusText: httpErr.statusText,
                body: httpErr.error,
            },
        });
    }
}

export function customErrorHandlerFactory(envInjector: EnvironmentInjector): ErrorHandler {
    return runInInjectionContext(envInjector, () => new CustomErrorHandler());
}
